/**
 * Uniswap V4 Configuration types and Zod schemas
 *
 * FR7: Smart defaults for fee splits and V4 hooks/extensions.
 * FR8: Progressive disclosure (simple vs advanced mode).
 *
 * Schemas aligned with clanker SDK contracts:
 * - Vault: packages/clanker-sdk/src/config/clankerTokenV4.ts
 * - DevBuy: packages/clanker-sdk/src/config/clankerTokenV4.ts
 * - Presale: packages/clanker-sdk/src/v4/extensions/presale.ts
 * - Airdrop: deferred to Story 2.7 (requires Merkle tree)
 */

import { z } from "zod";

// ─── Enums ───────────────────────────────────────────────────────

export const ExtensionTypeSchema = z.enum(["Vault", "Airdrop", "DevBuy", "Presale"]);
export type ExtensionType = z.infer<typeof ExtensionTypeSchema>;

// ─── Extension Config (Discriminated Union) ──────────────────────

/**
 * Vault config — UI-friendly fields.
 * SDK expects: percentage (0-90), lockupDuration (seconds, min 7 days), vestingDuration (seconds).
 * UI collects days and transforms via transformV4ConfigForSdk().
 */
export const VaultConfigSchema = z.object({
	type: z.literal("Vault"),
	/** Percent of total supply allocated to the vault (0-90). */
	percentage: z
		.number()
		.min(1, { error: "Percentage must be at least 1%" })
		.max(90, { error: "Percentage cannot exceed 90%" }),
	/** Lock period in days (min 7). */
	lockDurationDays: z.number().min(7, { error: "Lock duration must be at least 7 days" }),
	/** Vesting period in days after lockup (0 = no vesting). */
	vestingDurationDays: z
		.number()
		.min(0, { error: "Vesting duration cannot be negative" })
		.default(0),
});

/**
 * Recipient entry for airdrop address list.
 */
export const AirdropRecipientSchema = z.object({
	address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, { error: "Invalid Ethereum address" }),
	amount: z.number().int().positive({ error: "Amount must be a positive integer" }),
});

export type AirdropRecipient = z.infer<typeof AirdropRecipientSchema>;

/**
 * Airdrop config — UI-friendly fields.
 * SDK expects: merkleRoot, amount, lockupDuration (seconds), vestingDuration (seconds).
 * UI collects recipients + days, transforms via transformV4ConfigForSdk().
 */
export const AirdropConfigSchema = z.object({
	type: z.literal("Airdrop"),
	/** List of recipients with addresses and token amounts. Validated as non-empty at submit time (V4ConfigStep.handleNext). */
	recipients: z.array(AirdropRecipientSchema),
	/** Lock period in days (min 1). */
	lockPeriodDays: z
		.number()
		.int()
		.min(1, { error: "Lock period must be at least 1 day" })
		.default(7),
	/** Vesting period in days after lockup (0 = no vesting). */
	vestingDays: z.number().int().min(0, { error: "Vesting period cannot be negative" }).default(0),
});

/**
 * DevBuy config — UI-friendly fields.
 * SDK expects: ethAmount (number > 0), poolKey (auto-filled), recipient (auto-filled).
 * No delay field exists in the SDK.
 */
export const DevBuyConfigSchema = z.object({
	type: z.literal("DevBuy"),
	/** Native currency amount to spend on initial purchase. */
	amount: z
		.number()
		.min(0.01, { error: "Amount must be at least 0.01" })
		.max(1000, { error: "Amount cannot exceed 1000" }),
});

/**
 * Presale config — UI-friendly fields.
 * SDK expects: minEthGoal, maxEthGoal, presaleDuration (seconds), presaleSupplyBps (basis points),
 * lockupDuration (seconds, min 7 days), vestingDuration (seconds).
 */
export const PresaleConfigSchema = z
	.object({
		type: z.literal("Presale"),
		/** Minimum native currency goal for presale to succeed. */
		minGoalEth: z.number().min(0.01, { error: "Minimum goal must be at least 0.01" }),
		/** Maximum native currency goal for presale. */
		maxGoalEth: z.number().min(0.01, { error: "Maximum goal must be at least 0.01" }),
		/** Presale duration in hours (min 1). */
		duration: z.number().min(1, { error: "Duration must be at least 1 hour" }),
		/** Percentage of supply allocated to presale (1-100). */
		supplyAllocationPercent: z
			.number()
			.min(1, { error: "Supply allocation must be at least 1%" })
			.max(100, { error: "Supply allocation cannot exceed 100%" })
			.default(50),
		/** Token lock period in days after presale (min 7). */
		lockPeriodDays: z.number().min(7, { error: "Lock period must be at least 7 days" }).default(7),
		/** Vesting period in days after lockup (0 = no vesting). */
		vestingDays: z.number().min(0, { error: "Vesting period cannot be negative" }).default(0),
	})
	.refine((data) => data.maxGoalEth >= data.minGoalEth, {
		error: "Maximum goal must be greater than or equal to minimum goal",
		path: ["maxGoalEth"],
	});

export const ExtensionConfigSchema = z.discriminatedUnion("type", [
	VaultConfigSchema,
	AirdropConfigSchema,
	DevBuyConfigSchema,
	PresaleConfigSchema,
]);

export type VaultConfig = z.infer<typeof VaultConfigSchema>;
export type AirdropConfig = z.infer<typeof AirdropConfigSchema>;
export type DevBuyConfig = z.infer<typeof DevBuyConfigSchema>;
export type PresaleConfig = z.infer<typeof PresaleConfigSchema>;
export type ExtensionConfig = z.infer<typeof ExtensionConfigSchema>;

// ─── Template Presets ────────────────────────────────────────────

export type TemplatePreset = {
	name: string;
	description: string;
	extensions: ExtensionConfig[];
};

export const STANDARD_LAUNCH: TemplatePreset = {
	name: "Standard Launch",
	description: "Token launch with MEV protection and fee collection",
	extensions: [],
};

export const VAULTED_LAUNCH: TemplatePreset = {
	name: "Vaulted Launch",
	description: "Lock a portion of token supply to show long-term commitment",
	extensions: [{ type: "Vault", percentage: 10, lockDurationDays: 30, vestingDurationDays: 0 }],
};

/** Community Airdrop template — enables airdrop extension with empty recipients for upload. */
export const COMMUNITY_AIRDROP: TemplatePreset = {
	name: "Community Airdrop",
	description: "Launch with community airdrop allocation",
	extensions: [{ type: "Airdrop", recipients: [], lockPeriodDays: 7, vestingDays: 0 }],
};

export const TEMPLATE_PRESETS = [STANDARD_LAUNCH, VAULTED_LAUNCH, COMMUNITY_AIRDROP] as const;

// ─── V4 Configuration Schema ─────────────────────────────────────

export const V4ConfigSchema = z
	.object({
		feeSplitCreator: z
			.number()
			.int()
			.min(0, { error: "Creator fee cannot be negative" })
			.max(100, { error: "Creator fee cannot exceed 100%" }),
		feeSplitTreasury: z
			.number()
			.int()
			.min(0, { error: "Treasury fee cannot be negative" })
			.max(100, { error: "Treasury fee cannot exceed 100%" }),
		feeSplitProtocol: z
			.number()
			.int()
			.min(0, { error: "Protocol fee cannot be negative" })
			.max(100, { error: "Protocol fee cannot exceed 100%" }),
		extensions: z.array(ExtensionConfigSchema),
	})
	.refine((data) => data.feeSplitCreator + data.feeSplitTreasury + data.feeSplitProtocol === 100, {
		error: "Fee splits must sum to exactly 100%",
		path: ["feeSplitCreator"],
	});

export type V4Config = z.infer<typeof V4ConfigSchema>;

// ─── Smart Defaults ──────────────────────────────────────────────

export const DEFAULT_V4_CONFIG: V4Config = {
	feeSplitCreator: 50,
	feeSplitTreasury: 30,
	feeSplitProtocol: 20,
	extensions: [],
};

// ─── SDK Transform ───────────────────────────────────────────────

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;

/**
 * SDK-compatible vault config shape.
 */
export type SdkVaultConfig = {
	percentage: number;
	lockupDuration: number;
	vestingDuration: number;
};

/**
 * SDK-compatible dev buy config shape.
 */
export type SdkDevBuyConfig = {
	ethAmount: number;
};

/**
 * SDK-compatible presale config shape.
 */
export type SdkPresaleConfig = {
	minEthGoal: number;
	maxEthGoal: number;
	presaleDuration: number;
	presaleSupplyBps: number;
	lockupDuration: number;
	vestingDuration: number;
};

/**
 * SDK-compatible airdrop config shape.
 * merkleRoot and amount are computed by Merkle tree generation (client-side).
 */
export type SdkAirdropConfig = {
	merkleRoot: `0x${string}`;
	amount: number;
	lockupDuration: number;
	vestingDuration: number;
};

/**
 * Result of transforming V4Config for the SDK.
 */
export type SdkV4Extensions = {
	vault: SdkVaultConfig | undefined;
	devBuy: SdkDevBuyConfig | undefined;
	presale: SdkPresaleConfig | undefined;
	airdrop: SdkAirdropConfig | undefined;
};

/**
 * Airdrop Merkle data generated client-side by createMerkleTree/createAirdrop.
 * Passed to transformV4ConfigForSdk to complete the airdrop config.
 */
export type AirdropMerkleData = {
	merkleRoot: `0x${string}`;
	amount: number;
};

/**
 * Transform UI-friendly V4Config extension values into SDK-expected shapes.
 *
 * - Vault: days → seconds, percentage stays as-is
 * - DevBuy: amount → ethAmount
 * - Presale: hours → seconds, percent → basis points, days → seconds
 * - Airdrop: days → seconds, merkleRoot + amount from pre-computed Merkle data
 */
export function transformV4ConfigForSdk(
	config: V4Config,
	airdropMerkleData?: AirdropMerkleData
): SdkV4Extensions {
	let vault: SdkVaultConfig | undefined;
	let devBuy: SdkDevBuyConfig | undefined;
	let presale: SdkPresaleConfig | undefined;
	let airdrop: SdkAirdropConfig | undefined;

	for (const ext of config.extensions) {
		switch (ext.type) {
			case "Vault":
				vault = {
					percentage: ext.percentage,
					lockupDuration: ext.lockDurationDays * SECONDS_PER_DAY,
					vestingDuration: ext.vestingDurationDays * SECONDS_PER_DAY,
				};
				break;
			case "DevBuy":
				devBuy = {
					ethAmount: ext.amount,
				};
				break;
			case "Presale":
				presale = {
					minEthGoal: ext.minGoalEth,
					maxEthGoal: ext.maxGoalEth,
					presaleDuration: ext.duration * SECONDS_PER_HOUR,
					presaleSupplyBps: ext.supplyAllocationPercent * 100,
					lockupDuration: ext.lockPeriodDays * SECONDS_PER_DAY,
					vestingDuration: ext.vestingDays * SECONDS_PER_DAY,
				};
				break;
			case "Airdrop":
				if (airdropMerkleData) {
					airdrop = {
						merkleRoot: airdropMerkleData.merkleRoot,
						amount: airdropMerkleData.amount,
						lockupDuration: ext.lockPeriodDays * SECONDS_PER_DAY,
						vestingDuration: ext.vestingDays * SECONDS_PER_DAY,
					};
				}
				break;
		}
	}

	return { vault, devBuy, presale, airdrop };
}
